import { ReactNode } from "react";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faCheck } from "@fortawesome/pro-regular-svg-icons";

import Icon from "common/components/Icon";
import Spinner from "common/components/Spinner";

import ErrorMessage from "client/common/components/errors/ErrorMessage";

import styles from "../../styles/BaseInput.module.less";

type OnChange = (val: string) => void;

export type InputError = string | { key: string; content: ReactNode };

type RenderControlProps = {
  onChange: OnChange;
  value: string;
  disabled: boolean;
};

type RenderInputProps = {
  onChange: (val: string) => void;
  value: string;
  className: string;
  disabled: boolean;
  /** icon to display before input */
  preIcon: ReactNode;
  /** inline status indicator */
  status: ReactNode;
};

export type InputProps = {
  /** Any custom classes to apply to the input container.  */
  className?: string;
  /** The value this input is currently set to. */
  value: string;
  /** Event to trigger if the value changes. */
  onChange: (value: string) => void;
  /** Icon to display before value input. */
  preIcon?: IconProp;
  /** If the input is disabled and should be displayed as such / changes not allowed.  */
  disabled?: boolean;
  /**
   * The status of the input. These roughly map to react-query statues and change
   * the color / status-icon accordingly.
   *  */
  status?: InputStatus;
  /** A list of errors to display below the input container */
  errors?: InputError[];
};

/** status that mirrors react-query status to display an indicator for various states  */
export type InputStatus = "idle" | "success" | "error" | "loading";

const statusExhaustiveCheck = (status: never) => {
  console.warn("unhandled input status: ", status);
  return null;
};

type StatusProps = {
  status: InputStatus;
};

const Status = ({ status }: StatusProps) => {
  switch (status) {
    case "loading":
      return <Spinner />;
    case "success":
      return <Icon icon={faCheck} className={styles.check} />;
    case "idle":
    case "error":
      return null;
    default:
      return statusExhaustiveCheck(status);
  }
};

type AnimationContainerProps = {
  label: string;
  className?: string;
  isActive?: boolean;
  children: React.ReactNode;
};

const AnimationContainer = ({
  label,
  className = "",
  isActive,
  children,
}: AnimationContainerProps) => (
  <div className={`${styles.labelContainer} ${className} ${isActive ? styles.isActive : ""}`}>
    <span className={`${styles.label} `}>{label}</span>
    {children}
  </div>
);

type BaseInputProps = Omit<InputProps, "className"> & {
  styles?: {
    container?: string;
    content?: string;
    control?: string;
  };
  isFocused?: boolean;
  useLabelWrapper?: boolean;
  /** custom UI to display input. This could just be the reset input for example */
  renderInputDisplay: (props: RenderInputProps) => ReactNode;
  /** called in root element, useful for defining an element to cover control space, defaults to no content
   * this is particularly helpful for controls such as `select` which aren't triggered by `label` */
  renderInputControl?: (props: RenderControlProps) => ReactNode;
};

const BaseInput = (props: BaseInputProps) => {
  const {
    status = "idle",
    preIcon,
    renderInputControl,
    renderInputDisplay,
    value,
    onChange,
    disabled = false,
    isFocused = false,
    useLabelWrapper = true,
    styles: propStyles,
    errors = [],
  } = props;

  const ControlWrapper = useLabelWrapper ? "label" : "div";

  return (
    <div className={`${styles.container} ${propStyles?.container ?? ""}`}>
      <ControlWrapper
        className={`${styles.control} ${
          errors.length ? styles["status-error"] : styles[`status-${status}`]
        } ${disabled ? styles.disabled : ""} ${value ? styles.hasValue : ""} ${
          isFocused ? styles.isFocused : ""
        } ${propStyles?.control ?? ""}`}
      >
        <div
          className={`${styles.content} ${propStyles?.content ?? ""}`}
          aria-hidden={!!renderInputControl}
        >
          {renderInputDisplay({
            onChange,
            value,
            className: styles.input,
            disabled,
            preIcon: !!preIcon && <Icon icon={preIcon} className={styles.preIcon} />,
            status: status !== "idle" && (
              <div className={styles.status}>
                <Status status={status} />
              </div>
            ),
          })}
        </div>
        {renderInputControl && renderInputControl({ onChange, disabled, value })}
      </ControlWrapper>
      <div>
        {errors.map((error) => (
          <ErrorMessage key={typeof error === "string" ? error : error.key}>
            {typeof error === "string" ? error : error.content}
          </ErrorMessage>
        ))}
      </div>
    </div>
  );
};

export default Object.assign(BaseInput, { AnimationContainer });
